import { Box, HStack, Text } from '@chakra-ui/react'
import { MapPin } from 'lucide-react'
import { memo, useEffect, useMemo } from 'react'
import { CircleMarker, MapContainer, Marker, TileLayer, Tooltip, useMap, useMapEvents } from 'react-leaflet'
import L from 'leaflet'
import { DEFAULT_SITE_PIN_CENTER } from '@/components/forms/parentTicket/dispatchSitePin'
import 'leaflet/dist/leaflet.css'

const sitePinIcon = L.divIcon({
  className: 'dispatch-site-pin-icon',
  html: `<span style="
    display:block;
    width:18px;
    height:18px;
    border-radius:50% 50% 50% 0;
    background:#185fa5;
    border:2px solid #fff;
    box-shadow:0 1px 4px rgba(0,0,0,.35);
    transform:rotate(-45deg);
  "></span>`,
  iconSize: [18, 18],
  iconAnchor: [9, 18],
})

export interface DispatchSitePinMapProps {
  latitude?: number | null
  longitude?: number | null
  /** When false, clicks/drags are ignored (confirmed or read-only). */
  interactive?: boolean
  isReadOnly?: boolean
  height?: string | number
  onPinChange?: (latitude: number, longitude: number) => void
}

function RecenterOnPin({ position }: { position: [number, number] | null }) {
  const map = useMap()
  useEffect(() => {
    if (!position) return
    try {
      map.setView(position, Math.max(map.getZoom(), 15), { animate: false })
    } catch {
      // Map may be tearing down during remount.
    }
  }, [map, position?.[0], position?.[1]])
  return null
}

function MapClickHandler({
  enabled,
  onPinChange,
}: {
  enabled: boolean
  onPinChange?: (latitude: number, longitude: number) => void
}) {
  useMapEvents({
    click(event) {
      if (!enabled || !onPinChange) return
      onPinChange(event.latlng.lat, event.latlng.lng)
    },
  })
  return null
}

export const DispatchSitePinMap = memo(function DispatchSitePinMap({
  latitude,
  longitude,
  interactive = true,
  isReadOnly = false,
  height = '320px',
  onPinChange,
}: DispatchSitePinMapProps) {
  const canEdit = interactive && !isReadOnly && Boolean(onPinChange)
  const hasPin =
    latitude != null &&
    Number.isFinite(Number(latitude)) &&
    longitude != null &&
    Number.isFinite(Number(longitude))

  const position: [number, number] | null = useMemo(() => {
    if (!hasPin) return null
    return [Number(latitude), Number(longitude)]
  }, [hasPin, latitude, longitude])

  const center = position ?? DEFAULT_SITE_PIN_CENTER

  return (
    <Box
      border="0.5px solid"
      borderColor="gray.200"
      borderRadius="md"
      overflow="hidden"
      mt={2}
      position="relative"
      zIndex={0}
      sx={{ isolation: 'isolate' }}
    >
      <HStack
        justify="space-between"
        px={3}
        py={1.5}
        bg="gray.50"
        borderBottom="0.5px solid"
        borderColor="gray.200"
      >
        <HStack spacing={1.5}>
          <MapPin size={13} color="#205BF3" />
          <Text fontSize="11px" fontWeight="500" color="gray.600">
            Site pin
          </Text>
        </HStack>
        <Text fontSize="10px" color="gray.500">
          {canEdit
            ? hasPin
              ? 'Drag pin or click map to adjust'
              : 'Click map to place pin'
            : hasPin
              ? 'Location confirmed'
              : 'Confirm after placing pin'}
        </Text>
      </HStack>
      <Box h={height}>
        <MapContainer
          center={center}
          zoom={hasPin ? 15 : 11}
          style={{ height: '100%', width: '100%' }}
          scrollWheelZoom={!isReadOnly}
        >
          <RecenterOnPin position={position} />
          <MapClickHandler enabled={canEdit} onPinChange={onPinChange} />
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          {position ? (
            canEdit ? (
              <Marker
                position={position}
                icon={sitePinIcon}
                draggable
                eventHandlers={{
                  dragend: (event) => {
                    const marker = event.target as L.Marker
                    const next = marker.getLatLng()
                    onPinChange?.(next.lat, next.lng)
                  },
                }}
              >
                <Tooltip direction="top" offset={[0, -12]} opacity={1}>
                  Dispatch site
                </Tooltip>
              </Marker>
            ) : (
              <CircleMarker
                center={position}
                radius={10}
                pathOptions={{ color: '#185fa5', fillColor: '#185fa5', fillOpacity: 0.9 }}
              >
                <Tooltip direction="top" offset={[0, -6]} opacity={1} permanent>
                  Dispatch site
                </Tooltip>
              </CircleMarker>
            )
          ) : null}
        </MapContainer>
      </Box>
    </Box>
  )
})
